/**
 * The demo's three actions, and an automation that is data.
 *
 * Task 0029 asks for three real actions — change a property, create an object,
 * navigate — and for a composed automation stored in the object store rather
 * than written as code. This file is the registration side; `main.ts` supplies
 * the `Effects` implementation, which is the only way any of them can touch
 * anything.
 *
 * Note what is *not* here: no `onClick`, no handler attached to a spec, no
 * string that gets evaluated. A button in a layout spec names one of these by
 * id and declares what it may do; the registry checks the two agree, and the
 * gate in `host.ts` makes an undeclared effect impossible rather than merely
 * discouraged.
 */
import {
  ROLES,
  ActionRegistry, MAX_STEPS, type ActionContext, type TInvocation,
} from '@morph/spec';

export const ACTIONS = {
  create: 'demo.create-object',
  markSeen: 'demo.mark-seen',
  setScore: 'demo.set-score',
  setDate: 'demo.set-date',
  openRelated: 'demo.open-related',
} as const;

/**
 * Register the demo's actions.
 *
 * Each declares the least it can do. `demo.create-object` claims only
 * `create-object` even though creating also opens the new object's lens,
 * because opening a lens moves the VIEW and writes nothing — and an action
 * forced to claim a write it does not perform would make every declaration
 * less meaningful.
 */
export function buildRegistry(): ActionRegistry {
  const r = new ActionRegistry();

  r.register({
    name: ACTIONS.create,
    effects: ['create-object'],
    // NO TITLE. Decision 0024 — the person names it by typing into the Title
    // field the new object's lens opens with focus in, and a literal `Untitled`
    // written here would be authored data nobody authored (and would sit in
    // front of what they type).
    run: async (c: ActionContext) => { await c.effects.createObject(''); },
  });

  r.register({
    name: ACTIONS.markSeen,
    effects: ['set-property'],
    // ITS FIRST ARGUMENT, when the spec declared one. `args` was read by
    // nothing anywhere — the button ran with a fixed value however the
    // invocation was written — so this is the consumer that makes the field
    // mean something. The fallback keeps every existing caller working.
    run: async (c: ActionContext) => {
      await c.effects.setProperty('status', c.args[0] ?? 'seen');
    },
  });

  /**
   * THE PARAMETERISED ROW BUTTON — Coda's, and the one the bar has been half on.
   *
   * `acceptance-coverage.mjs` has scored *"buttons that run actions"* as half
   * with a precise reason: no browser case drives a PARAMETERISED action. The
   * mechanism was never missing — `resolveInvocationArgs` has resolved
   * `valueFrom` against the live view state for some time, with its own bounds
   * check. What was missing was an action whose parameter TYPE matches the
   * slots a generated lens can declare.
   *
   * `demo.mark-seen` writes `status`, an **enum** role. `pickViewStateSlot`
   * emits `number`, `score` and `date` slots only, because @Codex's view-state
   * ruling refuses enum and text outright — *"the current content-free shape
   * carries no option values"*. So wiring the existing button to a slot would
   * have written a numeric threshold into an enum property.
   *
   * That is not a parameterised action; it is a type error the store refuses.
   * Task 0079 records this exact shape costing a browser check once: it picked
   * an *area* chooser, wrote a value the role does not offer, watched the store
   * refuse it, and **reported a working renderer as a broken one**.
   *
   * So: a button that writes the role the control is ABOUT. "Raise this row to
   * the threshold" is a real Coda row button, and the value comes from the
   * same slot the region filters read — one control, two consumers, which is
   * the whole claim `viewState` makes.
   *
   * **It throws when the control is empty.** `main.ts` states the contract:
   * *"Throwing is the channel the interface already has. `invoke` catches
   * anything an action's `run` throws and converts it into `action-failed`"*,
   * and `say` fires so a person gets the sentence rather than the code. The
   * alternative is `setProperty('score', undefined)` — a write that reports
   * success and erases a value, which is the worst shape a refusal can take.
   */
  r.register({
    name: ACTIONS.setScore,
    effects: ['set-property'],
    run: async (c: ActionContext) => {
      const value = c.args[0];
      if (typeof value !== 'number') {
        throw new Error(
          'Set the minimum score first — this button writes the control\'s value.');
      }
      await c.effects.setProperty('score', value);
    },
  });

  /**
   * The same button for a DATE control, and the reason there are two.
   *
   * An action's parameter type has to match the slot it reads. `set-score`
   * writes a `score`; every pool the demo ships declares a `date` slot, because
   * `rankRoles` weighs `score` and `date` equally and breaks the tie on
   * coverage. So `set-score` is correct and never fires, which is a capability
   * the product claims and nobody can reach.
   *
   * The alternative was to give a pool a higher-coverage `score` so the existing
   * wiring fires. That is tuning the data until a button works, and it would
   * have made the demo less like the pools it is modelled on to make a check
   * pass.
   *
   * **The vocabulary already had the name.** `MUTATION_ROLE_KINDS` declares
   * `set-date` for `date` roles beside `set-value` for text, number and score —
   * so writing a date is a first-class mutation this repo designed for, not a
   * special case invented to close a row.
   *
   * Throws on an empty control for the reason `set-score` does: `setProperty`
   * with `undefined` is a write that reports success and erases a value.
   */
  r.register({
    name: ACTIONS.setDate,
    effects: ['set-property'],
    run: async (c: ActionContext) => {
      const value = c.args[0];
      if (typeof value !== 'string' || value === '') {
        throw new Error(
          'Set the date filter first — this button writes the control\'s value.');
      }
      await c.effects.setProperty('date', value);
    },
  });

  r.register({
    name: ACTIONS.openRelated,
    effects: ['navigate'],
    run: async (c: ActionContext) => { await c.effects.navigate('relation.peer'); },
  });

  return r;
}

/**
 * "Create an object, then open what it relates to."
 *
 * A plain array. It is written to the object store as JSON and read back out;
 * nothing about it is a function, and there is deliberately no way to express a
 * branch or a loop. If that turns out to be too little, the answer is another
 * registered action, not a language — which is the same call decision 0004 made
 * about aggregations and 0014 repeats about behaviour.
 */
export const COMPOSED: readonly TInvocation[] = [
  { action: ACTIONS.create, effects: ['create-object'] },
  { action: ACTIONS.markSeen, effects: ['set-property'] },
];

export const AUTOMATION_LABEL = 'Automation';

/**
 * Read a stored automation back out of a property value.
 *
 * The value is user data that round-tripped through a store, so it is parsed
 * defensively: a malformed blob, a non-array, a step that is not an object, a
 * step naming no action, or a list longer than the registry will run, all yield
 * `undefined` rather than throwing or handing a half-list to the runner. The
 * registry checks each step again anyway — this is the cheap check in front of
 * the real one, not a replacement for it.
 */
export function parseAutomation(raw: unknown): TInvocation[] | undefined {
  if (typeof raw !== 'string') return undefined;
  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch { return undefined; }
  if (!Array.isArray(parsed) || parsed.length === 0 || parsed.length > MAX_STEPS) {
    return undefined;
  }
  const steps: TInvocation[] = [];
  for (const step of parsed) {
    if (!step || typeof step !== 'object' || Array.isArray(step)) return undefined;
    const s = step as Record<string, unknown>;
    if (typeof s['action'] !== 'string') return undefined;
    if (!Array.isArray(s['effects']) || s['effects'].length === 0) return undefined;
    if (!s['effects'].every((e) => typeof e === 'string')) return undefined;
    // ARGS, defensively. Dropping them silently was the previous behaviour and
    // the worst of the three failures in task 0084: a stored automation came
    // back as a DIFFERENT automation and nothing said so.
    // The element type EXACTLY, not a restatement of it. Spelling the shape by
    // hand re-declares `role?` as including `undefined`, which
    // `exactOptionalPropertyTypes` then refuses against the schema's own type —
    // a distinction this repo turns on and worth not restating.
    let args: NonNullable<TInvocation['args']>[number][] | undefined;
    if (s['args'] !== undefined) {
      if (!Array.isArray(s['args']) || s['args'].length > 4) return undefined;
      args = [];
      for (const rawArg of s['args']) {
        if (!rawArg || typeof rawArg !== 'object' || Array.isArray(rawArg)) return undefined;
        const a = rawArg as Record<string, unknown>;
        const keys = Object.keys(a);
        if (keys.some((key) => key !== 'role' && key !== 'valueFrom')) return undefined;
        // THE CLOSED VOCABULARY, not "a string". This is user data — a value on
        // an object, editable in the lens — and an authored invocation may only
        // name a role the vocabulary has. Accepting any string let
        // `constructor` and `toString` through, and role resolution then reads
        // an inherited property off a projected object instead of a role.
        // Found by @Codex.
        const hasRole = a['role'] !== undefined;
        const hasValueFrom = a['valueFrom'] !== undefined;
        if (hasRole === hasValueFrom) return undefined;
        if (hasRole
          && !(ROLES as readonly string[]).includes(a['role'] as string)) return undefined;
        if (hasValueFrom
            && (!Number.isInteger(a['valueFrom']) || (a['valueFrom'] as number) < 0
              || (a['valueFrom'] as number) > 7)) return undefined;
        args.push(hasRole
          ? { role: a['role'] as never }
          : { valueFrom: a['valueFrom'] as number });
      }
    }
    steps.push({ action: s['action'], effects: s['effects'] as TInvocation['effects'],
      ...(args !== undefined ? { args } : {}) });
  }
  return steps;
}

export const serializeAutomation = (steps: readonly TInvocation[]): string =>
  JSON.stringify(steps.map((s) => ({ action: s.action, effects: s.effects,
    ...(s.args !== undefined ? { args: s.args } : {}) })));
